import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { getUnreadCount as calculateUnreadCount, sortNotificationsByTime } from "./utils";
import notificationSaga from "../../modules/Notifications/notificatioSaga";
import useWebSocket from "../../hooks/useWebSocket";

export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useSelector((state) => state.authReducer);
  const { socket, on, emit } = useWebSocket();

  useEffect(() => {
    if (!user?.id) return;

    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const data = await notificationSaga.getNotifications(user.id);
        setNotifications(sortNotificationsByTime(data));
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [user?.id]);

  useEffect(() => {
    if (!socket || !user?.id) return;

    const handleNewNotification = (notification) => {
      setNotifications(prev => {
        const exists = prev.some(n => n.id === notification.id);
        if (exists) return prev;
        const updated = [notification, ...prev];
        return sortNotificationsByTime(updated);
      });
    };

    const handleNotificationRead = (data) => {
      setNotifications(prev => 
        prev.map(n => n.id === data.notificationId 
          ? { ...n, isRead: true } 
          : n
        )
      );
    };

    on('new_notification', handleNewNotification);
    on('notificationRead', handleNotificationRead);

    return () => {
      socket.off('new_notification', handleNewNotification);
      socket.off('notificationRead', handleNotificationRead);
    };
  }, [socket, user?.id]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationSaga.markAsRead(notificationId);
      emit('markAsRead', { notificationId });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, [emit]);

  const markAllAsRead = useCallback(async () => {
    try {
      if (!user?.id) return;
      
      await notificationSaga.markAllAsRead(user.id);
      setNotifications(prev => 
        sortNotificationsByTime(prev.map(n => ({ ...n, isRead: true })))
      );
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [user?.id]);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await notificationSaga.deleteNotification(notificationId);
      setNotifications(prev => 
        prev.filter(n => n.id !== notificationId)
      );
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }, []);

  const unreadCount = useMemo(
    () => calculateUnreadCount(notifications),
    [notifications]
  );

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    isEmpty: notifications.length === 0,
    allRead: unreadCount === 0,
  };
};

export default useNotifications;